import { useState, useEffect } from 'react';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const slides = [
    {
      title: "Smart Software Solutions for Modern Businesses",
      description: "We build secure, scalable applications, web platforms, enterprise software, and custom DPC / EMM solutions.",
      image: `${process.env.PUBLIC_URL}/images/slider/slider1.png`,
      alt: "Tracklio Hub - custom enterprise software and web application development",
    },
    {
      title: "Enterprise-Grade Security Solutions",
      description: "Protect your business with our advanced DPC and EMM platforms designed for maximum security and control.",
      image: `${process.env.PUBLIC_URL}/images/slider/slider2.png`,
      alt: "Tracklio Hub - enterprise DPC and EMM mobile device security platform",
    },
    {
      title: "Scalable Web Platforms That Grow With You",
      description: "Build your digital presence with cutting-edge web technologies and cloud-native architectures.",
      image: `${process.env.PUBLIC_URL}/images/slider/slider3.png`,
      alt: "Tracklio Hub - scalable cloud-native web platform development",
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const styles = {
    heroContainer: {
      position: 'relative',
      width: '100%',
      height: isMobile ? 'auto' : 'calc(100vh - 64px)',
      overflow: 'hidden',
      marginTop: '64px',
    },
    slideWrapper: {
      position: 'relative',
      width: '100%',
      height: '100%',
      paddingTop: isMobile ? '56.25%' : '0', // 16:9 aspect ratio for mobile
    },
    slide: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
    },
    backgroundImage: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
    },
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.55) 100%)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: isMobile ? '0 24px' : '0 80px',
      boxSizing: 'border-box',
    },
    slideTitle: {
      color: '#ffffff',
      fontSize: isMobile ? 'clamp(22px, 6vw, 30px)' : 'clamp(32px, 4vw, 52px)',
      fontWeight: 800,
      lineHeight: 1.2,
      maxWidth: '800px',
      margin: 0,
      marginBottom: isMobile ? '10px' : '20px',
      textShadow: '0 2px 12px rgba(0,0,0,0.4)',
    },
    slideDescription: {
      color: '#f3f4f6',
      fontSize: isMobile ? '14px' : '18px',
      lineHeight: 1.7,
      maxWidth: '600px',
      margin: 0,
      textShadow: '0 1px 8px rgba(0,0,0,0.4)',
    },
    srOnly: {
      position: 'absolute',
      width: '1px',
      height: '1px',
      padding: 0,
      margin: '-1px',
      overflow: 'hidden',
      clip: 'rect(0, 0, 0, 0)',
      whiteSpace: 'nowrap',
      border: 0,
    },
    navButton: {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      backgroundColor: 'rgba(255,255,255,0.3)',
      border: 'none',
      borderRadius: '50%',
      width: isMobile ? '35px' : '50px',
      height: isMobile ? '35px' : '50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: 3,
      transition: 'background-color 0.3s ease',
    },
    dots: {
      position: 'absolute',
      bottom: isMobile ? '15px' : '30px',
      left: '50%',
      transform: 'translateX(-50%)',
      display: 'flex',
      gap: isMobile ? '8px' : '10px',
      zIndex: 3,
    },
    dot: {
      width: isMobile ? '8px' : '12px',
      height: isMobile ? '8px' : '12px',
      borderRadius: '50%',
      border: '2px solid #ffffff',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
  };

  return (
    <section
      style={styles.heroContainer}
      id="home"
      aria-roledescription="carousel"
      aria-label="Tracklio Hub - Smart software solutions for modern businesses"
    >
      <div style={styles.slideWrapper}>
        {slides.map((slide, index) => (
          <div
            key={slide.title}
            style={{
              ...styles.slide,
              opacity: currentSlide === index ? 1 : 0,
              transform: currentSlide === index ? 'scale(1)' : 'scale(1.1)',
              pointerEvents: currentSlide === index ? 'auto' : 'none',
            }}
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} of ${slides.length}`}
            aria-hidden={currentSlide !== index}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              style={styles.backgroundImage}
              loading={index === 0 ? 'eager' : 'lazy'}
              fetchpriority={index === 0 ? 'high' : 'low'}
              decoding="async"
              width="1920"
              height="1080"
            />

            {/* Visible, crawlable heading + description over the slide image */}
            <div style={styles.overlay}>
              {index === 0 ? (
                <h1 style={styles.slideTitle}>{slide.title}</h1>
              ) : (
                <p style={styles.slideTitle} role="heading" aria-level="2">
                  {slide.title}
                </p>
              )}
              <p style={styles.slideDescription}>{slide.description}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Live region so screen readers announce slide changes without being noisy */}
      <p style={styles.srOnly} aria-live="polite">
        {slides[currentSlide].title}
      </p>

      {/* Previous Button */}
      <button
        type="button"
        style={{
          ...styles.navButton,
          left: isMobile ? '8px' : '20px'
        }}
        onClick={prevSlide}
        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.5)'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
        aria-label="Previous slide"
        title="Previous slide"
      >
        <svg width={isMobile ? "16" : "24"} height={isMobile ? "16" : "24"} viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" aria-hidden="true" focusable="false">
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>

      {/* Next Button */}
      <button
        type="button"
        style={{
          ...styles.navButton,
          right: isMobile ? '8px' : '20px'
        }}
        onClick={nextSlide}
        onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.5)'}
        onMouseLeave={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.3)'}
        aria-label="Next slide"
        title="Next slide"
      >
        <svg width={isMobile ? "16" : "24"} height={isMobile ? "16" : "24"} viewBox="0 0 24 24" fill="none" stroke="#ffffff" strokeWidth="2" aria-hidden="true" focusable="false">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      {/* Dots Navigation */}
      <div style={styles.dots} role="tablist" aria-label="Choose slide">
        {slides.map((slide, index) => (
          <div
            key={slide.title}
            role="tab"
            tabIndex={0}
            aria-selected={currentSlide === index}
            aria-label={`Go to slide ${index + 1}: ${slide.title}`}
            style={{
              ...styles.dot,
              backgroundColor: currentSlide === index ? '#ffffff' : 'transparent',
            }}
            onClick={() => goToSlide(index)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                goToSlide(index);
              }
            }}
          />
        ))}
      </div>
    </section>
  );
};

export default Hero;